<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';
import { dateLong, timeRange } from './eventTime.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const event = inject('tiqCurrentEvent', null);
// A visitor never meets the slot's placeholder: with no real event
// to render it draws nothing, and only the editor keeps the stand-in.
const editing = inject('tiqEditorCanvas', false);

// The placeholder image is the template designer's stand-in, so the
// editor canvas is not a grey rectangle while the template is being
// built. A real event's own photo always wins.
const heroImage = computed(() => event?.hero_image || props.config.placeholder_image || '');
const title = computed(() => event?.title || 'Event title');
const tag = computed(() => event?.tag || event?.audience || '');
const dateStr = computed(() => dateLong(event));
const timeStr = computed(() => timeRange(event));
const minHeight = computed(() => `${Number(props.config.height) || 480}px`);
</script>

<template>
    <section v-if="event || editing" :id="config.anchor_id || null"
             class="th-block th-ehero"
             :style="{ ...styleVars(config), minHeight }">
        <div class="th-ehero__bg th-media th-media--dark">
            <img v-if="heroImage" :src="heroImage" alt="">
        </div>
        <div class="th-ehero__scrim"></div>
        <div class="th-wrap th-ehero__inner">
            <p v-if="tag" class="th-eyebrow th-ehero__tag">{{ tag }}</p>
            <h1 class="th-h1 th-h1--article th-ehero__title">{{ title }}</h1>
            <div v-if="dateStr || timeStr" class="th-mono th-mono--caps th-ehero__meta">
                <span v-if="dateStr">{{ dateStr }}</span>
                <span v-if="dateStr && timeStr" class="th-ehero__dot">·</span>
                <span v-if="timeStr">{{ timeStr }}</span>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-ehero {
    display: flex;
    align-items: flex-end;
    overflow: hidden;
    background: var(--block-bg, #2a2f2a);
    color: var(--block-text, #fff);
}
/* Two classes, not one: `.tpl-trailhead .th-media` weighs two and
   loads after this, and it set this layer back to position: relative
   at zero height — so an event's photo was never on screen at all. */
.th-ehero .th-ehero__bg { position: absolute; inset: 0; border-radius: 0; }
.th-ehero__scrim {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(10, 12, 10, .78), rgba(10, 12, 10, 0) 68%);
}
.th-ehero__inner {
    position: relative;
    width: 100%;
    padding-top: 120px;
    padding-bottom: clamp(32px, 5vw, 64px);
}
.th-ehero__tag { color: var(--text-eyebrow, #fff); }
.th-ehero__title { max-width: 24ch; margin-bottom: 14px; color: var(--text-heading, inherit); }
.th-ehero__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    color: var(--text-sub, rgba(255, 255, 255, .88));
}
.th-ehero__dot { opacity: .6; }
</style>
